import { useEffect, useLayoutEffect, useMemo, useState } from 'react'

import { useNavigate, useSearchParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { DateRangePicker, IRange } from 'features/range-date/date-range-picker'
import { prepareDate } from 'features/range-date/lib'

import findTicketFormStore from 'entities/ticket/models/find-ticket/find-ticket-store'

import {
  CalendarApi,
  ICalendarPrice,
  ICalendarPriceResponse,
} from 'shared/api/Calendar'
import { SelectedDaysDifficult } from 'shared/ui/month-wrapper'

import { HeaderCalendar } from './components/header/header'
import { onRangeDifficultRoute } from './lib'
import { getFocusDate } from './lib/focusDate'
import s from './styles.module.scss'

const CalendarRangePage = observer(() => {
  const navigate = useNavigate()

  const [params] = useSearchParams()

  const isSearch = !!params.get('page')
  const difficult = !!params.get('difficult')

  const [calendarPrices, setCalendarPrices] = useState<
    ICalendarPriceResponse[] | null
  >(null)

  const indexDirection = difficult ? parseInt(params.get('difficult')!) : 0

  const selectedDays = findTicketFormStore?.getFormFindTicketDirections()

  const onRangeDateHandler = (data: IRange) => {
    if (difficult) {
      onRangeDateHandlerDifficult(data)
      return
    }

    findTicketFormStore.onSetRangeDate({
      rangeData: data,
      indexDirect: 0,
      isCopy: isSearch,
    })

    navigate(-1)
  }

  const onRangeDateHandlerDifficult = (date: IRange) => {
    const otherSegments = [
      ...(findTicketFormStore?.formFindTicket?.directions || {}),
    ]

    const results = onRangeDifficultRoute({
      date,
      segments: otherSegments,
      difficultIndex: indexDirection,
    })

    if (results) {
      findTicketFormStore.onChangeDirectionDates(results)
    }

    navigate(-1)
  }

  const getCalendarPrices = async (data: ICalendarPrice) => {
    const response = await CalendarApi.getCalendarPrice({ ...data })

    setCalendarPrices(response.data.data)
  }

  const onTriggerDate = (date?: string) => {
    getCalendarPrices({
      to: firstDirection?.to?.cityIataCode,
      from_: firstDirection?.from?.cityIataCode,
      date_: date,
    })
  }

  const firstDirection = isSearch
    ? findTicketFormStore?.copyFormFindTicket &&
      findTicketFormStore?.copyFormFindTicket?.directions[0]
    : findTicketFormStore.formFindTicket.directions[0]

  // reset scroll while unmounting, in the same commit as the previous page:
  // navigate(-1) is async, so scrolling in the handlers showed the calendar
  // jumping to the top before the transition
  useLayoutEffect(() => () => window.scrollTo(0, 0), [])

  useEffect(() => {
    if (
      !difficult &&
      firstDirection?.from?.cityIataCode &&
      firstDirection?.to?.cityIataCode
    ) {
      getCalendarPrices({
        to: firstDirection.to?.cityIataCode,
        from_: firstDirection.from?.cityIataCode,
        date_: firstDirection?.date,
      })
    }
  }, [])

  const selectedDate: IRange = difficult
    ? {
        startDate: selectedDays[indexDirection]?.date,
      }
    : {
        startDate: firstDirection?.date,
        endDate: firstDirection?.back_date,
      }

  // focus only on open: confirming updates the store, and refocusing would
  // start a smooth window scroll that continues on the page we navigate back to
  const [focusDate] = useState(() => getFocusDate(selectedDays, !!difficult))

  const selectedDaysList: SelectedDaysDifficult[] | undefined = useMemo(
    () =>
      selectedDays?.map(({ date }) => ({
        fullDate: date ? prepareDate(String(date)) : '',
        isActive: date === selectedDays[indexDirection]?.date,
      })),
    [selectedDays, indexDirection],
  )

  return (
    <div className={s.container}>
      <div className={s.contentPop}>
        <DateRangePicker
          header={<HeaderCalendar difficult={difficult} />}
          focusDate={focusDate}
          indexDirection={indexDirection}
          difficult={difficult}
          selectedDate={selectedDate}
          selectedDates={selectedDaysList}
          onChange={onRangeDateHandler}
          calendarPrices={calendarPrices}
          triggerCalendar={onTriggerDate}
          direction={firstDirection || {}}
          showRange={!difficult}
          typeCalendar={difficult ? 'aviaDifficult' : 'avia'}
        />
      </div>
    </div>
  )
})

export default CalendarRangePage
